import React, { FC } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, EffectFade } from 'swiper/modules'
import banner from '@images/home/banner.jpg'
import banner1 from '@images/home/banner1.png'

import 'swiper/css'
import 'swiper/css/effect-fade'

import { ChevronNextIcon } from '@svgs'
import { Button } from 'src/components/ui'
import { useTranslation } from 'react-i18next'
import { useGetBannersQuery } from 'src/redux/api'
import { HOW_IT_WORKS_ID } from '../how/HowItWorks'

interface IMainBanner {}

const BACKGROUNDS = [banner, banner1]

interface ISlide {
  key: string
  title: string
  description: string
  background: string
  showButton: boolean
}

const BannerSlide: FC<{ slide: ISlide }> = ({ slide }) => {
  const { t } = useTranslation()
  return (
    <section className="overflow-hidden bg-inverse rounded-3xl relative min-h-[13.125rem] flex items-center p-8 px-[3.125rem]">
      <div className="flex flex-col relative z-10">
        <div className="flex flex-col gap-2 items-start text-white">
          <h2 className="text-2xl font-black leading-7">{slide.title}</h2>
          <p className="self-stretch mb-2 text-base">{slide.description}</p>
          {/* The instruction is the "how it works" section further down the
              same page; the button used to do nothing */}
          {slide.showButton && (
            <Button
              variant={'secondary'}
              iconAfter={<ChevronNextIcon />}
              onClick={() =>
                document
                  .getElementById(HOW_IT_WORKS_ID)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            >
              {t('banner.button')}
            </Button>
          )}
        </div>
      </div>
      <div className="absolute inset-0 size-full pointer-events-none touch-none">
        <div className="absolute top-0 right-0 w-[57.53%] h-full">
          <img loading="lazy" src={slide.background} alt="" className="object-cover size-full" />
          <div className="absolute top-0 left-0 h-full w-1/2 bg-banner-gradient" />
        </div>
      </div>
    </section>
  )
}

const MainBanner: FC<IMainBanner> = () => {
  const { t } = useTranslation()
  const { data } = useGetBannersQuery()

  // Render admin-managed (CMS) banners when present. With the seek/offer mode
  // gone (38.2) the fallback carousel tells both stories: one seeker slide,
  // one owner slide.
  const cmsBanners = data?.items ?? []
  const defaultSlides: ISlide[] = [
    {
      key: 'default-1',
      title: t('banner.title_1'),
      description: t('banner.description_1'),
      background: banner,
      showButton: true,
    },
    {
      key: 'owner-1',
      title: t('banner.owner_title_1'),
      description: t('banner.owner_description_1'),
      background: banner1,
      showButton: false,
    },
  ]
  const slides: ISlide[] =
    cmsBanners.length > 0
      ? cmsBanners.map((b, i) => ({
          key: b.id,
          title: b.title,
          description: b.content,
          background: BACKGROUNDS[i % BACKGROUNDS.length],
          showButton: false,
        }))
      : defaultSlides

  return (
    // Swiper ships position:relative + z-index:1 on its root, which painted
    // the banner OVER dropdown menus opened from the hero search right above
    // it (they also sit at z-index 1 but earlier in the DOM). Drop the banner
    // to z-0 so any popover above stays readable. The wrapper hides it on
    // phones: Swiper's own display:block beat the "hidden" class, and the
    // banner pushed every listing below the first screen.
    <div className="hidden md:block">
      <Swiper
        className="w-full !z-0"
        autoplay={{
          delay: 4000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
        }}
        modules={[EffectFade, Autoplay]}
        effect="fade"
        speed={600}
        spaceBetween={0}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.key}>
            <BannerSlide slide={slide} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

export default MainBanner
